import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import { GetApp } from "../../../App"
import { Router } from "../../../App/Routes"
import { useDataStore } from "../../../Data/Context"
import { isShipUser, mergeLiveMembership } from "../../../Data/ShipUserMembership"
import { useEntity, useExtras, useQuery, required } from "../../../Data/useStore"
import { useRetainedEntities } from "../../../Data/useRetainedEntities"
import { AppRoutes, AppViewRoutes } from "../../App/Router"
import type { ISettings, IShip } from "../../../Types"
import type { AppViewProps } from "../Params"
import { SelectedShipContext, type SelectedShipData, withSelectedShipSettings } from "./Context"
import { useSelectedShipLoader } from "./Loader"
import { selectedShipId } from "./Route"

function isShip(value: unknown): value is IShip {
  return typeof value === "object"
    && value !== null
    && typeof (value as Partial<IShip>).id === "number";
}

function isSettings(value: unknown): value is ISettings {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Partial<ISettings>;
  return typeof row.id === "number"
    && typeof row.shipId === "number"
    && typeof row.settingType === "number";
}

export function SelectedShipDataView({ route, children }: AppViewProps & { children?: ReactNode }) {
  const app = GetApp();
  const store = useDataStore();
  const routeShipId = selectedShipId(route);
  const entityId = routeShipId ?? 0;
  const rawShip = useEntity(store, "Ship", entityId);
  const shipUsers = useQuery(store, "ShipUser", { shipId: required(routeShipId) });
  const settings = useQuery(store, "Settings", { shipId: required(routeShipId) });
  const extras = useExtras(store, "Ship", { shipId: routeShipId });
  const responseShip = isShip(extras.ship) ? extras.ship : undefined;
  const responseSettings = useMemo(
    () => Array.isArray(extras.settings) ? extras.settings.filter(isSettings) : [],
    [extras.settings],
  );
  const responseShipUsers = useMemo(
    () => Array.isArray(extras.shipUsers) ? extras.shipUsers.filter(isShipUser) : [],
    [extras.shipUsers],
  );
  const mergedShipUsers = useMemo(
    () => mergeLiveMembership(responseShipUsers, shipUsers),
    [responseShipUsers, shipUsers],
  );
  const activeShipUserId = typeof extras.activeShipUserId == "number"
    ? extras.activeShipUserId
    : undefined;
  const [storeBackedShipId, setStoreBackedShipId] = useState<number>();
  useEffect(() => {
    if (routeShipId != undefined
      && activeShipUserId != undefined
      && shipUsers.some((user) => user.id == activeShipUserId)) {
      setStoreBackedShipId(routeShipId);
    }
  }, [activeShipUserId, routeShipId, shipUsers]);
  const selectedShipUsers = storeBackedShipId == routeShipId
    ? shipUsers
    : mergedShipUsers;
  const selectedRawShip = storeBackedShipId == routeShipId
    ? rawShip
    : responseShip ?? rawShip;
  const selectedSettings = storeBackedShipId == routeShipId
    ? settings
    : responseSettings;
  const shipUser = activeShipUserId == undefined
    ? undefined
    : selectedShipUsers.find((user) => user.id == activeShipUserId);
  const [loadedShipId, setLoadedShipId] = useState<number>();
  const navigateApp = useMemo(() => Router.navigate(AppViewRoutes, app.updateRoute), [app.updateRoute]);

  const reset = useCallback(() => {
    setLoadedShipId(undefined);
    setStoreBackedShipId(undefined);
  }, []);

  useRetainedEntities(["Ship", "ShipUser", "Settings"], routeShipId != undefined, routeShipId);

  const load = useCallback(async (shipId: number) => {
    await store.fetchQueryStrict("Ship", { shipId });
    const exactShip = store.getExtras("Ship", { shipId }).ship;
    return store.getEntity("Ship", shipId) ?? (isShip(exactShip) ? exactShip : undefined);
  }, [store]);
  const onLoaded = useCallback((shipId: number) => {
    setLoadedShipId(shipId);
  }, []);
  const onMissing = useCallback(
    () => navigateApp(AppRoutes.accountSettings),
    [navigateApp],
  );

  useSelectedShipLoader({
    shipId: routeShipId,
    loadedShipId,
    load,
    onLoaded,
    onMissing,
  });

  const ship = useMemo(
    () => selectedRawShip ? withSelectedShipSettings(selectedRawShip, selectedSettings) : undefined,
    [selectedRawShip, selectedSettings],
  );
  const bundleLoaded = loadedShipId == routeShipId && routeShipId != undefined;

  const value: SelectedShipData = {
    shipId: routeShipId,
    ship: bundleLoaded ? ship : undefined,
    shipUser: bundleLoaded ? shipUser : undefined,
    shipUsers: bundleLoaded ? selectedShipUsers : undefined,
    activeShipUserId: bundleLoaded ? activeShipUserId : undefined,
    bundleLoaded,
    reset,
  };
  return <SelectedShipContext.Provider value={value}>{children}</SelectedShipContext.Provider>;
}
